import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import submit from "submit-form";
import Modal from "../../../../../parts/modal-new";
import SvgIcon from "../../../../../parts/SvgIcon";
import scriptLoader from "../../../../../utils/scriptLoader";

export const CustomMealModal = ({ locals }) => {
  const { mealPlan, query } = locals;
  const { athlete, mealPlan: mealPlanId } = query;
  const { weeks } = mealPlan;
  const existingMeals = weeks[0]?.days[0]?.meals || [];
  const orderedMeals = existingMeals.slice().sort((a, b) => a.order - b.order);
  const initialMealsWithOrder = orderedMeals.map(({ type, order }) => ({
    type,
    order,
  }));
  const basicMealTypes = ["Breakfast", "Lunch", "Snack", "Dinner"];
  const [state, setState] = useState({
    name: "",
    meals: initialMealsWithOrder,
    isReordered: false,
  });
  const { name, meals, isReordered } = state;

  /**
   * Reorder
   */
  useEffect(() => {
    if (document.querySelector(".principal-grabber-div")) {
      scriptLoader(
        "https://cdn.jsdelivr.net/npm/sortablejs@latest/Sortable.min.js",
        () => {
          const dragImage = document.createElement("img");
          dragImage.src =
            "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";
          const sortable = new Sortable(
            document.querySelector(".principal-grabber-div"),
            {
              animation: 350,
              handle: ".drag-wrapper",
              setData(dataTransfer) {
                dataTransfer.setDragImage(dragImage, 0, 0);
              },
              onEnd: (e) => {
                if (e.oldIndex === e.newIndex) return;
                const items = e.from.querySelectorAll(":scope > div");
                e.from.insertBefore(
                  e.item,
                  items[e.oldIndex + (e.oldIndex > e.newIndex)]
                );
                const ordered = Array.from(items).map((element, index) => {
                  const mealName = element.querySelector(".span-type");
                  return {
                    type: mealName?.textContent || "",
                    order: index + 1,
                  };
                });

                setState((prevState) => ({
                  ...prevState,
                  meals: ordered,
                  isReordered: true,
                }));

                return false;
              },
            }
          );
        }
      );
    }
  }, []);

  /**
   * handle the inputs value
   * @param {*} e
   */
  const handleInputChange = (e) => {
    let { value } = e.target;

    // removing special characters but allowing spaces
    value = value.replace(/[^\p{L}\p{N} ]/gu, "");
    setState({ ...state, name: value });
  };

  /**
   * Add custom meal
   */
  const handleAddCustomMeal = () => {
    if (meals.some(({ type }) => type.toLowerCase() === name.toLowerCase())) {
      const body = {
        type: "error",
        message: `A meal with this name already exists. Please choose a different name.`,
      };
      return submit(`/actions/common/toast`, {
        method: "POST",
        body,
      });
    }

    const newMeal = { type: name.trim(), order: meals.length + 1, foods: [] };

    setState((prevState) => {
      const updatedMeals = [...prevState.meals, newMeal];
      return {
        ...prevState,
        name: "",
        meals: updatedMeals,
      };
    });
  };

  /**
   * Save the custom meals
   * @returns
   */
  const handleSave = () => {
    const newMeals = meals.filter(
      (meal) => !existingMeals.some(({ type }) => type === meal?.type)
    );
    const hasNewMeals = newMeals.length > 0;
    if (!hasNewMeals && !isReordered) {
      return;
    }

    const handle = hasNewMeals
      ? isReordered
        ? "add-reorder"
        : "add"
      : "reorder";

    const body = {
      newMeals,
      bodyAction: "handle-custom-meal",
      handle,
      ...(isReordered && { newOrder: meals }),
    };

    submit(
      `/actions/meal-planner/update-days-meal-plan?mealPlanId=${mealPlanId}&proxyAthlete=${athlete}`,
      {
        method: "POST",
        body,
      }
    );
  };

  /**
   * Function to delete a custom meal
   * @param {*} e
   */
  const handleDeleteCustomMeal = (e, meal) => {
    const mealExists = existingMeals.some((m) => m.type === meal);
    //if the custom meal was recently added, we only have to update the state
    if (!mealExists) {
      setState((prevState) => {
        const updatedMeals = prevState.meals.filter((m) => m.type !== meal);
        return {
          ...prevState,
          meals: updatedMeals,
        };
      });
    } else {
      e.preventDefault();
      e.stopPropagation();
      if (
        process.browser &&
        confirm(
          `Are you sure you want to delete this meal and all its contents?`
        )
      ) {
        const body = {
          meal,
          bodyAction: "handle-custom-meal",
          handle: "delete",
        };

        submit(
          `/actions/meal-planner/update-days-meal-plan?mealPlanId=${mealPlanId}&proxyAthlete=${athlete}`,
          {
            method: "POST",
            body,
          }
        );
      }
    }
  };

  return (
    <Modal className="custom-meal-modal" title={"Create Custom Meal"}>
      <div className="body">
        <div>
          <span className="opacity-70 text-[0.75rem]">Meals</span>
        </div>
        <div className="order principal-grabber-div">
          {meals.map(({ type }, index) => (
            <div key={`${type}-${index}`} className="row">
              <div className="name">
                <span className="span-type">{type}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="drag-wrapper">
                  <SvgIcon src="/img/new-icons/menu.svg" />
                </div>
                {!basicMealTypes.includes(type) && (
                  <a
                    onClick={(e) => {
                      handleDeleteCustomMeal(e, type);
                    }}
                  >
                    <SvgIcon
                      src="/img/new-icons/red-trash.svg"
                      noFilterOnLight={true}
                    />
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
        <div className="inputs">
          <div>
            <span className="opacity-70 text-[0.75rem]">New Meal</span>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="text"
              placeholder={`Enter Meal Name`}
              value={name}
              onChange={handleInputChange}
            />

            <button
              data-button-type={"outline"}
              onClick={handleAddCustomMeal}
              disabled={meals.length > 5 || name.length === 0}
            >
              <span>Add</span>
            </button>
          </div>
        </div>
        <div className="footer">
          <button data-button-type="primary" onClick={handleSave}>
            Save
          </button>
        </div>
      </div>
    </Modal>
  );
};

CustomMealModal.propTypes = {
  locals: PropTypes.object,
};
